import { createContext, useContext, useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("nexo_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (res.ok) return res.json();
          throw new Error("Invalid token");
        })
        .then((data) => {
          setUser(data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem("nexo_token");
          setToken(null);
          setUser(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Erro no login");
    }
    const data = await res.json();
    localStorage.setItem("nexo_token", data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, password }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Erro no registro");
    }
    const data = await res.json();
    localStorage.setItem("nexo_token", data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  }, []);

  const completeOnboarding = useCallback(async (categories) => {
    const res = await fetch(`${API_URL}/api/onboarding/complete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ categories }),
    });
    if (!res.ok) throw new Error("Falha ao completar onboarding");
    const data = await res.json();
    setUser((prev) => ({ ...prev, onboarding_completed: true }));
    return data;
  }, [token]);

  // ONBOARDING-PROFILES — aplica um mega-template (categorias + hábitos +
  // lembretes + projetos + budget) num batch e marca onboarding_completed.
  const applyProfile = useCallback(async (profileKey) => {
    const res = await fetch(`${API_URL}/api/onboarding/apply_profile`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ profile_key: profileKey }),
    });
    if (!res.ok) throw new Error("Falha ao aplicar perfil");
    const data = await res.json();
    setUser((prev) => ({ ...prev, onboarding_completed: true }));
    return data;
  }, [token]);

  const logout = useCallback(() => {
    localStorage.removeItem("nexo_token");
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, completeOnboarding, applyProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
